"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Table, Button, Badge, Alert } from "reactstrap"
import { FaPlus, FaEdit, FaTrash, FaUser, FaPhone, FaIdCard, FaCalendarAlt, FaUndo } from "react-icons/fa"
import { toast } from "react-toastify"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { Staff, TokenData, PaginatedResponse } from "./types"
import PreviousStaffModal from "./PreviousStaffModal"

interface Props {
  token: string
  tokenData: TokenData
  onAdd: () => void
  onEdit: (item: Staff) => void
  refresh: boolean
}

const StaffTab: React.FC<Props> = ({ token, tokenData, onAdd, onEdit, refresh }) => {
  const [staff, setStaff] = useState<Staff[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [previousStaffModalOpen, setPreviousStaffModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

  const fetchStaff = async (page = 1, search = "") => {
    try {
      setLoading(true)
      const response: PaginatedResponse<Staff> = await StaffCarService.getStaff(token, page, 10, search)
      setStaff(response.results)
      setTotalPages(response.total_pages)
      setTotalCount(response.count)
      setCurrentPage(page)
    } catch (error) {
      console.error("Failed to fetch staff:", error)
      toast.error("Failed to load staff data")
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    const timeout = setTimeout(() => {
      setCurrentPage(1)
      fetchStaff(1, value)
    }, 300)
    
    setSearchTimeout(timeout)
  }

  useEffect(() => {
    fetchStaff(currentPage, searchQuery)
  }, [token, refresh])

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this staff member?")) {
      try {
        await StaffCarService.deleteStaff(token, id)
        toast.success("Staff member deleted successfully")
        fetchStaff(currentPage)
      } catch (error) {
        console.error("Failed to delete staff:", error)
        toast.error("Failed to delete staff member")
      }
    }
  }

  // Registration is intentionally never locked once the allotted staff
  // limit is reached — the focal person can keep registering staff; any
  // excess beyond the limit is simply flagged here and charged later during
  // badge printing instead of being blocked at registration time.
  const canAddMore = true
  const isOverLimit = tokenData.max_staff > 0 && totalCount >= tokenData.max_staff
  const overLimitCount = Math.max(0, totalCount - tokenData.max_staff)

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" style={{ width: "2.5rem", height: "2.5rem" }} role="status">
          <span className="visually-hidden">Loading staff...</span>
        </div>
        <p className="mt-3 text-muted">Loading staff information...</p>
      </div>
    )
  }

  return (
    <div>
      <style>{`
        .professional-header {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid #e2e8f0;
        }
        
        .add-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        
        .add-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .add-btn:disabled {
          background: #e2e8f0;
          color: #64748b;
          box-shadow: none;
        }
        
        .professional-table {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }
        
        .professional-table thead th {
          background: #f8fafc;
          border: none;
          font-weight: 600;
          color: #475569;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px 12px;
        }
        
        .professional-table tbody td {
          border: none;
          padding: 16px 12px;
          vertical-align: middle;
          font-size: 0.875rem;
        }
        
        .professional-table tbody tr {
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }
        
        .professional-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .action-btn {
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 0.75rem;
          border: 1px solid;
          transition: all 0.2s ease;
        }
        
        .action-btn:hover {
          transform: translateY(-1px);
        }
        
        .edit-btn {
          color: #10b981;
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.1);
        }
        
        .edit-btn:hover {
          background: #10b981;
          color: white;
        }
        
        .delete-btn {
          color: #ef4444;
          border-color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }
        
        .delete-btn:hover {
          background: #ef4444;
          color: white;
        }
        
        .staff-info {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
        }
        
        .staff-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          object-fit: cover;
          background: #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }
        
        .pagination-modern .page-link {
          border: none;
          border-radius: 6px;
          margin: 0 2px;
          padding: 8px 12px;
          color: #64748b;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        
        .pagination-modern .page-item.active .page-link {
          background: #3b82f6;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        
        .pagination-modern .page-link:hover {
          background: #f1f5f9;
          color: #3b82f6;
        }
        
        .empty-state {
          text-align: center;
          padding: 48px 24px;
          background: #f8fafc;
          border-radius: 12px;
          border: 2px dashed #cbd5e1;
        }
        
        .limit-alert {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border: 1px solid #f59e0b;
          border-radius: 8px;
          color: #92400e;
        }
        
        .name-badge {
          font-family: 'Courier New', monospace;
          font-weight: bold;
          color: #1e293b;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
        }
      `}</style>

      <div className="professional-header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div>
            <h5 className="mb-2 fw-bold text-dark">Staff Registry</h5>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary bg-opacity-10 text-warning px-3 py-2">
                {totalCount} / {tokenData.max_staff} Registered
              </span>
              
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              color="outline-primary"
              onClick={() => setPreviousStaffModalOpen(true)}
              title="Bring back staff who represented this company in a previous exhibition"
            >
              <FaUndo className="me-2" size={12} />
              Bring Back Previous Staff
            </Button>
            <Button
              className="add-btn"
              onClick={onAdd}
              disabled={!canAddMore}
              title="Register new staff member"
            >
              <FaPlus className="me-2" size={12} />
              Add Staff
            </Button>
          </div>
        </div>
        <div>
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="form-control"
            style={{
              borderRadius: "8px",
              padding: "10px 14px",
              fontSize: "0.875rem",
              border: "1px solid #cbd5e1",
              backgroundColor: "white"
            }}
          />
        </div>
      </div>

      {isOverLimit && (
        <Alert className="limit-alert mb-4">
          <div className="d-flex align-items-center">
            <FaUser className="me-2" size={16} />
            <strong>Note:</strong> You have reached the {tokenData.max_staff} allotted staff slots
            {overLimitCount > 0 && ` (${overLimitCount} over)`}. You can keep registering staff — any
            excess beyond the allotted slots will be charged during badge printing.
          </div>
        </Alert>
      )}

      <PreviousStaffModal
        isOpen={previousStaffModalOpen}
        toggle={() => setPreviousStaffModalOpen(false)}
        token={token}
        tokenData={tokenData}
        onSuccess={() => fetchStaff(currentPage)}
      />

      {staff.length === 0 ? (
        <div className="empty-state">
          <FaUser className="mb-3 text-muted" size={48} />
          <h6 className="text-muted mb-2">No Staff Members Registered</h6>
          <p className="text-muted small mb-0">Start by registering your first staff member for the exhibition.</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="professional-table mb-0">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Registration Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="staff-avatar me-3">
                          {member.photo_path ? (
                            <img
                              src={member.photo_path}
                              alt={`${member.first_name} ${member.last_name}`}
                              className="staff-avatar"
                            />
                          ) : (
                            <FaUser size={16} />
                          )}
                        </div>
                        <div>
                          <div className="fw-medium text-dark">
                            {member.first_name} {member.last_name}
                          </div>
                          
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="staff-info">
                        <FaPhone size={12} />
                        <span>{member.phone || "No phone provided"}</span>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted">{member.email || "No email provided"}</span>
                    </td>
                    <td>
                      <div className="staff-info">
                        <FaCalendarAlt size={12} />
                        <span>
                          {member.created_at
                            ? new Date(member.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "N/A"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          className="action-btn"
                          size="sm"
                          color="success"
                          onClick={() => onEdit(member)}
                          title="Edit staff details"
                        >
                          <FaEdit size={11} />
                        </Button>
                        <Button
                          className="action-btn "
                          size="sm"
                          color="danger"
                          onClick={() => handleDelete(member.id)}
                          title="Remove staff member"
                        >
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted small">
                Showing page {currentPage} of {totalPages}
              </div>
              <nav>
                <ul className="pagination pagination-sm pagination-modern mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchStaff(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page = i + 1
                    if (totalPages > 5) {
                      if (currentPage > 3) {
                        page = currentPage - 2 + i
                      }
                      if (currentPage > totalPages - 2) {
                        page = totalPages - 4 + i
                      }
                    }
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => fetchStaff(page)}>
                          {page}
                        </button>
                      </li>
                    )
                  })}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => fetchStaff(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default StaffTab
